import { cn } from '@heidi/utils';
import { times } from 'lodash-es';
import { type MutableRefObject } from 'react';

interface Props {
  barsRef?: MutableRefObject<HTMLDivElement[]>;
  isSilent?: boolean;
  audioReady?: boolean;
  isError?: boolean;
  className?: string;
  /** Compact size for use in tight spaces like sidebar indicators */
  size?: 'default' | 'compact';
  /**
   * Number of bars to render. With 3, drivers map their 5-way level data
   * onto the centre three bands so the mid-tall-mid arc and per-band
   * responsiveness are preserved (V2 session-header design).
   */
  barCount?: 3 | 5;
}

const sizeStyles = {
  default: {
    container: 'h-5 w-11 gap-0.5 px-1',
    bar: 'max-h-5 min-h-2 w-1',
  },
  compact: {
    container: 'h-3 w-4 gap-[1px] px-0',
    bar: 'max-h-3 min-h-[3px] w-[2px]',
  },
};

export const BarsBase = ({
  barsRef,
  isSilent,
  audioReady,
  isError,
  className,
  size = 'default',
  barCount = 5,
}: Props) => {
  const styles = sizeStyles[size];

  return (
    <div
      className={cn(
        'flex items-center justify-around overflow-hidden py-px',
        styles.container,
        className
      )}
    >
      {times(barCount).map((i) => (
        <div
          key={i}
          ref={(el) => {
            if (barsRef) barsRef.current[i] = el as HTMLDivElement;
          }}
          className={cn(
            'flex-1 rounded-full bg-validation-success',
            styles.bar,
            ((isSilent && audioReady) || isError) && '!bg-validation-destructive'
          )}
          style={{ transition: 'height 0.1s ease-out' }}
        />
      ))}
    </div>
  );
};
